import {
  Table,
  Pagination,
  Group,
  Text,
  Stack,
  Skeleton,
  Center,
  Box,
} from "@mantine/core"
import type { DataTableProps } from "./types"

const SORT_ARROW_UP = (
  <span style={{ marginLeft: 4, fontSize: "0.65em", color: "var(--mantine-color-blue-6)" }}>
    &#9650;
  </span>
)

const SORT_ARROW_DOWN = (
  <span style={{ marginLeft: 4, fontSize: "0.65em", color: "var(--mantine-color-blue-6)" }}>
    &#9660;
  </span>
)

const SORT_ARROW_INACTIVE = (
  <span style={{ marginLeft: 4, fontSize: "0.65em", color: "var(--mantine-color-gray-3)" }}>
    &#9651;
  </span>
)

export default function DataTable<T>({
  columns,
  data,
  total,
  page,
  pageSize,
  sort,
  sortDir,
  loading = false,
  emptyMessage,
  keyExtractor,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onRowClick,
  actions,
}: DataTableProps<T>) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleHeaderClick = (col: (typeof columns)[number]) => {
    if (!col.sortable || !onSortChange) return
    const dir = col.key === sort && sortDir === "asc" ? "desc" : "asc"
    onSortChange(col.key, dir)
  }

  const sortIndicator = (key: string) => {
    if (!onSortChange) return null
    if (key !== sort) return SORT_ARROW_INACTIVE
    return sortDir === "asc" ? SORT_ARROW_UP : SORT_ARROW_DOWN
  }

  const cellPad = (idx: number, total: number): React.CSSProperties => ({
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: idx === 0 ? 10 : 5,
    paddingRight: idx === total - 1 ? 10 : 5,
  })

  const actionsPad: React.CSSProperties = {
    paddingTop: 5,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 10,
  }

  const renderSkeletonRows = () => {
    const count = Math.min(pageSize, 8)
    return Array.from({ length: count }).map((_, i) => (
      <Table.Tr key={`skel-${i}`}>
        {columns.map((col, idx) => (
          <Table.Td key={col.key} style={cellPad(idx, columns.length)}>
            <Skeleton height={18} radius="sm" />
          </Table.Td>
        ))}
        {actions && (
          <Table.Td style={actionsPad}>
            <Group gap="xs">
              <Skeleton height={28} circle />
              <Skeleton height={28} circle />
              <Skeleton height={28} circle />
            </Group>
          </Table.Td>
        )}
      </Table.Tr>
    ))
  }

  const renderDataRows = () => {
    if (data.length === 0) {
      return (
        <Table.Tr key="empty">
          <Table.Td colSpan={columns.length + (actions ? 1 : 0)} style={{ padding: "5px 10px" }}>
            <Center py={60}>
              <Stack align="center" gap="xs">
                <Text size="40px" c="gray.3">
                  <i className="fa fa-clipboard" />
                </Text>
                <Text c="dimmed" size="sm">
                  {emptyMessage}
                </Text>
              </Stack>
            </Center>
          </Table.Td>
        </Table.Tr>
      )
    }

    return data.map((item) => {
      const key = keyExtractor(item)
      return (
        <Table.Tr
          key={key}
          {...(onRowClick
            ? {
                style: { cursor: "pointer" },
                onClick: () => onRowClick(item),
              }
            : {})}
        >
          {columns.map((col, idx) => (
            <Table.Td key={col.key} style={{ ...cellPad(idx, columns.length), ...(col.width ? { width: col.width } : undefined) }}>
              {col.render ? col.render(item) : String((item as Record<string, unknown>)[col.key] ?? "")}
            </Table.Td>
          ))}
          {actions && (
            <Table.Td onClick={(e) => e.stopPropagation()} style={{ ...actionsPad, textAlign: "right", whiteSpace: "nowrap" }}>
              {actions(item)}
            </Table.Td>
          )}
        </Table.Tr>
      )
    })
  }

  return (
    <Stack>
      <Box style={{ borderRadius: "var(--mantine-radius-md)", overflow: "hidden", border: "1px solid var(--mantine-color-gray-3)" }}>
        <Table.ScrollContainer minWidth={600}>
          <Table>
            <Table.Thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "var(--mantine-color-gray-0)",
              }}
            >
              <Table.Tr>
                {columns.map((col, idx) => (
                  <Table.Th
                    key={col.key}
                    style={{
                      ...cellPad(idx, columns.length),
                      cursor: col.sortable ? "pointer" : undefined,
                      width: col.width,
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleHeaderClick(col)}
                  >
                    {col.label}
                    {col.sortable && sortIndicator(col.key)}
                  </Table.Th>
                ))}
                {actions && (
                  <Table.Th style={{ ...actionsPad, textAlign: "right", width: 160, whiteSpace: "nowrap" }}>&nbsp;</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? renderSkeletonRows() : renderDataRows()}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Box>

      <Group justify="space-between" align="center" px="sm">
        {total > 0 ? (
          <Text size="sm" c="dimmed">
            {pageSize * (page - 1) + 1}–{Math.min(page * pageSize, total)} de {total}
          </Text>
        ) : (
          <span />
        )}
        {totalPages > 1 && (
          <Pagination total={totalPages} value={page} onChange={onPageChange} size="sm" />
        )}
      </Group>
    </Stack>
  )
}
